import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useReducer } from "react";
import Footer from "../SharedComponents/Footer";
import Header from "../SharedComponents/Header";
import "../../assets/stylesheets/HomePage/HomePages.scss";

import { useFlags, withLDProvider } from "launchdarkly-react-client-sdk";
import {
  getDashboard,
  getDashboardMultiContract,
  getAddonStatus,
} from "../../api/makeAPIRequest";
import { CONTRACT_STATUS_DESC, isArchived } from "../../constants/Contract";
import { UtagTracking } from "../../utils/utag-tracking";
import Loader from "../SharedComponents/Loader";
import ActiveAndPriorApplications from "./HomePages/ActiveAndPriorApplications";
import OOSPage from "./OOSPage";
import HomePageGreeting from "./HomePages/HomePageGreeting";

// Helper function to check if ANY addon is still pending
// Returns true if at least one addon has a pending status
const hasAnyAddonPending = (addonStatuses) => {
  if (
    !addonStatuses ||
    !Array.isArray(addonStatuses) ||
    addonStatuses.length === 0
  )
    return false;

  return addonStatuses.some((addon) => {
    const status = addon.contractstatusdesc?.toLowerCase() || "";
    // Check if status contains "pending" or "review" (not approved/declined)
    return status.includes("pending") || status.includes("review");
  });
};

function HomePage(props) {
  //OKTA
  const { authState } = useOktaAuth();

  //LAUNCH DARKLY FLAGS
  const { candidateGlobalOos } = useFlags();

  //INITIAL STATE
  const initialState = {
    agencyname: "",
    agencynumber: "",
    applicationStatus: "",
    contracteffectivedate: "",
    contractDesc: "",
    contract_id: "",
    prevSub: "",
    firstname: "",
    lastname: "",
    nyregs: false,
    phonenumber: "",
    candidateid: "",
    contracttypeid: "",
    mmlisallowed: "",
    financingallowed: "",
    contractList: [],
    archiveList: [],
    moreActions: false,
    lastchangedate: "",
    mmlis: "",
    mmid: "",
    financingonly: "",
    mmlisregistrationdate: "",
    hasSSN: false,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "onChange":
        return { ...state, [action.id]: action.value };
      case "filterContracts":
        // Separate contracts based on status and addon status
        const activeContracts = [];
        const archivedContracts = [];

        action?.value?.forEach((contract) => {
          const isContractArchived = isArchived(contract.contractstatusdesc);
          const hasAddons =
            contract.mmlis ||
            contract.financingonly ||
            contract.cria ||
            contract.ia;

          // If contract is "Approved by Home Office" and has addons
          if (
            contract.contractstatusdesc ===
              CONTRACT_STATUS_DESC.APPROVED_BY_HOME_OFFICE &&
            hasAddons
          ) {
            // If ANY addon is still pending, keep in Active Applications
            // Otherwise (all approved/declined), move to Prior Applications
            if (
              contract.addonStatuses &&
              hasAnyAddonPending(contract.addonStatuses)
            ) {
              activeContracts.push(contract);
            } else {
              // No pending addons - move to Prior Applications
              archivedContracts.push(contract);
            }
          } else if (isContractArchived) {
            archivedContracts.push(contract);
          } else {
            activeContracts.push(contract);
          }
        });

        let filteredState = {
          ...state,
          contractList: activeContracts?.sort(function (a, b) {
            const contractStatusTypes = [
              CONTRACT_STATUS_DESC.RETURNED_TO_CANDIDATE,
              CONTRACT_STATUS_DESC.APPLICATION_IN_PROGRESS,
              CONTRACT_STATUS_DESC.INVITATION_SENT,
              CONTRACT_STATUS_DESC.MMLIS_Pending,
            ];

            const aStatusIndex = contractStatusTypes.indexOf(
              a.contractstatusdesc,
            );
            const bStatusIndex = contractStatusTypes.indexOf(
              b.contractstatusdesc,
            );

            if (aStatusIndex === bStatusIndex)
              return b.contractstatusdesc - a.contractstatusdesc;

            return bStatusIndex - aStatusIndex;
          }),
          archiveList: archivedContracts,
        };
        return filteredState;

      case "get_dashboard_data":
        return {
          ...state,
          agencyname: action.agencyname,
          agencynumber: action.agencynumber,
          applicationStatus: action.applicationStatus,
          contractDesc: action.contractDesc,
          contract_id: action.contract_id,
          prevSub: action.prevSub,
          firstname: action.firstname,
          lastname: action.lastname,
          nyregs: action.nyregs,
          phonenumber: action.phonenumber,
          candidateid: action.candidateid,
          contracttypeid: action.contracttypeid,
          mmlisallowed: action.mmlisallowed,
          financingallowed: action.financingallowed,
          contracteffectivedate: action.contracteffectivedate,
          lastchangedate: action.lastchangedate,
          mmlis: action.mmlis,
          mmid: action.mmid,
          financingonly: action.financingonly,
          mmlisregistrationdate: action.mmlisregistrationdate,
          hasSSN: action.hasSSN,
        };
      case "get_dashboard_data_error":
        return {
          ...state,
          applicationStatus: action.applicationStatus,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const isSafari = () => {
    const userAgent = navigator.userAgent;
    return /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  };

  const getHomePageData = async () => {
    try {
      //need delay because of safari
      if (isSafari()) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Update the document title using the browser API
      let result = await getDashboard(authState.idToken.claims.sub);

      let multiResult = await getDashboardMultiContract(
        authState.idToken.claims.sub,
      );

      // Fetch addon statuses for contracts with addons that are "Approved by Home Office"
      const contractsWithAddonStatus = await Promise.all(
        multiResult.map(async (contract) => {
          const hasAddons =
            contract.mmlis ||
            contract.financingonly ||
            contract.cria ||
            contract.ia;
          const isApprovedByHomeOffice =
            contract.contractstatusdesc ===
            CONTRACT_STATUS_DESC.APPROVED_BY_HOME_OFFICE;

          if (hasAddons && isApprovedByHomeOffice) {
            try {
              const addonStatuses = await getAddonStatus(
                authState.idToken.claims.sub,
                contract.contractid,
              );
              return { ...contract, addonStatuses };
            } catch (error) {
              console.error(
                "Error fetching addon status for contract:",
                contract.contractid,
                error,
              );
              return { ...contract, addonStatuses: null };
            }
          }
          return contract;
        }),
      );

      dispatch({
        type: "filterContracts",
        value: contractsWithAddonStatus,
      });

      if (result === CONTRACT_STATUS_DESC.INVITATION_SENT) {
        dispatch({
          type: "get_dashboard_data_error",
          applicationStatus: CONTRACT_STATUS_DESC.INVITATION_SENT,
        });
      } else {
        dispatch({
          type: "get_dashboard_data",
          agencyname: result.agencyname,
          agencynumber: result.agencynumber,
          applicationStatus: result.contractstatusdesc,
          contractDesc: result.contracttypedesc,
          contract_id: result.contractid,
          prevSub: result.prevsubmission,
          firstname: result.firstname,
          lastname: result.lastname,
          nyregs: result.nyregs,
          phonenumber: result.phonenumber,
          candidateid: result.candidateid,
          contracttypeid: result.contracttypeid,
          mmlisallowed: result.mmlisallowed,
          financingallowed: result.financingallowed,
          contracteffectivedate: result.contracteffectivedate,
          lastchangedate: result.lastchangedate,
          mmlis: result.mmlis,
          mmid: result.mmid,
          financingonly: result.financingonly,
          mmlisregistrationdate: result.mmlisregistrationdate,
          hasSSN: result.hasssn,
        });
        UtagTracking.sendPageNameView(
          "homepage",
          result.candidateid,
          result.agencynumber,
          result.contracttypeid,
        );
      }
    } catch (err) {
      console.log(err);

      dispatch({
        type: "get_dashboard_data_error",
        applicationStatus: CONTRACT_STATUS_DESC.INVITATION_SENT,
      });
    }
  };

  useEffect(() => {
    if (state.agencynumber === "") {
      if (
        candidateGlobalOos === false ||
        typeof candidateGlobalOos === "undefined"
      ) {
        getHomePageData();
      }
    }
  }, []);

  return (
    <div id="homepage">
      {candidateGlobalOos === true && <OOSPage />}
      {(candidateGlobalOos === false ||
        typeof candidateGlobalOos === "undefined") && (
        <>
          <Loader
            customClass={state.applicationStatus === "" ? "" : "hidden"}
          />
          <Header />
          <div>
            {state.applicationStatus !== "" && (
              <>
                <HomePageGreeting
                  firstname={state.firstname}
                  lastname={state.lastname}
                />
                <ActiveAndPriorApplications
                  mmid={state.mmid}
                  contractList={state.contractList}
                  archiveList={state.archiveList}
                  oktasubid={authState?.accessToken?.claims?.uid}
                  hasSSN={state.hasSSN}
                />
              </>
            )}
          </div>
          {state.applicationStatus !== "" && (
            <div className="container">
              <Footer />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default withLDProvider({
  clientSideID: window.REACT_APP_LAUNCH_DARKLY_CLIENT_ID,
})(HomePage);
